import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  Upload as UploadIcon, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle,
  File, Trash2, Download, Clock, Database, TrendingUp, RefreshCw,
  FileText, Table, X, Eye, ChevronRight, HardDrive, Users, Scissors,
  Building2, Info, FileDown, Shield, Copy, Check
} from 'lucide-react'
import { uploadAPI } from '../services/api'
import { useAuthStore, useAlertStore, ROLES } from '../store'
import toast from 'react-hot-toast'

// Historial de cargas demo
const DEMO_HISTORY = [
  { id: 1, filename: 'pacientes_dic_2024.xlsx', type: 'pacientes', size: '245 KB', date: '2024-12-28', records: 156, status: 'success' },
  { id: 2, filename: 'cirugias_semana52.csv', type: 'cirugias', size: '89 KB', date: '2024-12-27', records: 45, status: 'success' },
  { id: 3, filename: 'hospitales_update.xlsx', type: 'hospitales', size: '12 KB', date: '2024-12-26', records: 6, status: 'success' },
  { id: 4, filename: 'pacientes_error.csv', type: 'pacientes', size: '178 KB', date: '2024-12-25', records: 0, status: 'error', error: 'Formato invalido en columna NSS' },
]

// Contador animado
const AnimatedCounter = ({ value, duration = 1000, showDashOnZero = false }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const end = parseInt(value) || 0
    if (end === 0) {
      setCount(0)
      return
    }

    const startTime = Date.now()
    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(end * easeOut))
      if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [value, duration])

  if (showDashOnZero && (!value || value === 0)) return <span>--</span>
  return <span>{count}</span>
}

// KPI Card
const KPICard = ({ title, value, subtitle, icon: IconComponent, color, showDashOnZero = false }) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-emerald-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
  }

  const showDash = showDashOnZero && (!value || value === 0)

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color]} p-5 text-white shadow-lg`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <div className="text-4xl font-bold mb-1 tracking-tight">
            {showDash ? '--' : <AnimatedCounter value={value} />}
          </div>
          <p className="text-white/70 text-xs">{subtitle}</p>
        </div>
        <div className="p-3 bg-white/20 rounded-xl">
          <IconComponent className="w-6 h-6" />
        </div>
      </div>
    </div>
  )
}

// Tipos de archivo soportados
const FILE_TYPES = [
  { id: 'pacientes', name: 'Pacientes', icon: Users, description: 'Lista de pacientes en espera', color: 'blue' },
  { id: 'cirugias', name: 'Cirugias', icon: Scissors, description: 'Programacion de cirugias', color: 'emerald' },
  { id: 'hospitales', name: 'Hospitales', icon: HardDrive, description: 'Datos de hospitales', color: 'purple' },
]

// Modal de preview
const PreviewModal = ({ file, fileType, onClose, onConfirm, uploading }) => {
  if (!file) return null

  const typeInfo = FILE_TYPES.find(t => t.id === fileType) || FILE_TYPES[0]
  const IconComponent = typeInfo.icon

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Confirmar carga</h2>
                <p className="text-sm text-gray-500">Revisa los detalles antes de continuar</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Archivo</span>
              <span className="font-medium text-gray-800">{file.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Tamano</span>
              <span className="font-medium text-gray-800">{(file.size / 1024).toFixed(2)} KB</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Tipo de datos</span>
              <span className="flex items-center gap-2 font-medium text-gray-800">
                <IconComponent className="w-4 h-4" />
                {typeInfo.name}
              </span>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Importante</p>
              <p className="text-sm text-amber-700">Los registros existentes seran actualizados y los nuevos seran insertados.</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <UploadIcon className="w-5 h-5" />
                Confirmar carga
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Definición de campos por tipo de carga
const FILE_FIELDS = {
  pacientes: {
    name: 'Pacientes',
    description: 'Lista de pacientes en espera de cirugía',
    fields: [
      { name: 'NSS', type: 'texto', required: true, description: 'Número de Seguridad Social (11 dígitos)', example: '12345678901' },
      { name: 'Nombre', type: 'texto', required: true, description: 'Nombre completo del paciente', example: 'Juan Pérez García' },
      { name: 'Fecha_Nacimiento', type: 'fecha', required: true, description: 'Formato YYYY-MM-DD', example: '1985-03-15' },
      { name: 'Sexo', type: 'texto', required: true, description: 'M o F', example: 'M' },
      { name: 'Diagnostico', type: 'texto', required: true, description: 'Diagnóstico principal', example: 'Colelitiasis' },
      { name: 'Procedimiento', type: 'texto', required: true, description: 'Procedimiento quirúrgico requerido', example: 'Colecistectomía laparoscópica' },
      { name: 'Prioridad', type: 'texto', required: true, description: 'Alta, Media o Baja', example: 'Media' },
      { name: 'Especialidad', type: 'texto', required: true, description: 'Especialidad médica', example: 'Cirugía General' },
      { name: 'Hospital_ID', type: 'número', required: true, description: 'ID del hospital asignado', example: '1' },
      { name: 'Fecha_Registro', type: 'fecha', required: true, description: 'Fecha de registro en lista de espera', example: '2024-09-20' },
      { name: 'Telefono', type: 'texto', required: false, description: 'Teléfono de contacto', example: '555-123-4567' },
      { name: 'Domicilio', type: 'texto', required: false, description: 'Dirección del paciente', example: 'Calle Principal 123' },
    ]
  },
  cirugias: {
    name: 'Cirugías',
    description: 'Programación y registro de cirugías',
    fields: [
      { name: 'Paciente_NSS', type: 'texto', required: true, description: 'NSS del paciente', example: '12345678901' },
      { name: 'Procedimiento', type: 'texto', required: true, description: 'Procedimiento a realizar', example: 'Apendicectomía' },
      { name: 'Fecha_Programada', type: 'fecha', required: true, description: 'Fecha programada YYYY-MM-DD', example: '2024-12-30' },
      { name: 'Hora_Inicio', type: 'hora', required: true, description: 'Hora de inicio HH:MM', example: '08:00' },
      { name: 'Duracion_Estimada', type: 'número', required: true, description: 'Duración en minutos', example: '120' },
      { name: 'Hospital_ID', type: 'número', required: true, description: 'ID del hospital', example: '1' },
      { name: 'Quirofano', type: 'texto', required: true, description: 'Número de quirófano', example: 'Q-3' },
      { name: 'Estado', type: 'texto', required: true, description: 'programada, en_curso, completada, diferida, cancelada', example: 'programada' },
      { name: 'Cirujano', type: 'texto', required: false, description: 'Nombre del cirujano principal', example: 'Dr. García' },
      { name: 'Anestesiologo', type: 'texto', required: false, description: 'Nombre del anestesiólogo', example: 'Dra. López' },
      { name: 'Motivo_Diferimiento', type: 'texto', required: false, description: 'Si aplica, motivo del diferimiento', example: '' },
      { name: 'Observaciones', type: 'texto', required: false, description: 'Notas adicionales', example: '' },
    ]
  },
  hospitales: {
    name: 'Hospitales',
    description: 'Datos de unidades médicas',
    fields: [
      { name: 'Clave', type: 'texto', required: true, description: 'Clave única del hospital', example: 'HGZ-53' },
      { name: 'Nombre', type: 'texto', required: true, description: 'Nombre completo', example: 'HGZ No. 53 Los Reyes' },
      { name: 'Tipo', type: 'texto', required: true, description: 'HGZ, HGR, UMAE, etc.', example: 'HGZ' },
      { name: 'Delegacion', type: 'texto', required: true, description: 'Delegación', example: 'Oriente CDMX' },
      { name: 'Quirofanos', type: 'número', required: true, description: 'Número de quirófanos', example: '8' },
      { name: 'Quirofanos_Activos', type: 'número', required: true, description: 'Quirófanos operativos', example: '7' },
      { name: 'Camas_Cirugia', type: 'número', required: false, description: 'Camas para cirugía', example: '45' },
      { name: 'Latitud', type: 'número', required: false, description: 'Coordenada latitud', example: '19.3647' },
      { name: 'Longitud', type: 'número', required: false, description: 'Coordenada longitud', example: '-98.9969' },
      { name: 'Direccion', type: 'texto', required: false, description: 'Dirección completa', example: 'Av. Principal 100, Col. Centro' },
      { name: 'Telefono', type: 'texto', required: false, description: 'Teléfono de contacto', example: '555-100-2000' },
    ]
  }
}

// Componente de instrucciones y plantillas
const FormatInstructions = ({ fileType }) => {
  const [showDetails, setShowDetails] = useState(false)
  const [copied, setCopied] = useState(false)
  const fieldInfo = FILE_FIELDS[fileType]

  // Generar CSV de plantilla
  const generateTemplate = () => {
    const headers = fieldInfo.fields.map(f => f.name).join(',')
    const examples = fieldInfo.fields.map(f => f.example).join(',')
    const csvContent = `${headers}\n${examples}`

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `plantilla_${fileType}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Plantilla descargada')
  }

  // Copiar encabezados
  const copyHeaders = () => {
    const headers = fieldInfo.fields.map(f => f.name).join(',')
    navigator.clipboard.writeText(headers)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success('Encabezados copiados')
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-800">Formato para {fieldInfo.name}</h3>
              <p className="text-sm text-blue-600">{fieldInfo.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyHeaders}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copiado' : 'Copiar encabezados'}
            </button>
            <button
              onClick={generateTemplate}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <FileDown className="w-4 h-4" />
              Descargar plantilla
            </button>
          </div>
        </div>
      </div>

      {/* Resumen de campos */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-500">
            {fieldInfo.fields.filter(f => f.required).length} campos requeridos, {fieldInfo.fields.filter(f => !f.required).length} opcionales
          </span>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            {showDetails ? 'Ocultar detalles' : 'Ver todos los campos'}
            <ChevronRight className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Vista compacta de campos requeridos */}
        {!showDetails && (
          <div className="flex flex-wrap gap-2">
            {fieldInfo.fields.filter(f => f.required).map(field => (
              <span
                key={field.name}
                className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
              >
                {field.name}
              </span>
            ))}
            {fieldInfo.fields.filter(f => !f.required).length > 0 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                +{fieldInfo.fields.filter(f => !f.required).length} opcionales
              </span>
            )}
          </div>
        )}

        {/* Vista detallada */}
        {showDetails && (
          <div className="mt-4 space-y-2 animate-fadeIn">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 pb-2 border-b">
              <div className="col-span-2">Campo</div>
              <div className="col-span-1">Tipo</div>
              <div className="col-span-1">Req.</div>
              <div className="col-span-4">Descripción</div>
              <div className="col-span-4">Ejemplo</div>
            </div>
            {fieldInfo.fields.map(field => (
              <div key={field.name} className="grid grid-cols-12 gap-2 text-sm py-2 border-b border-gray-50 hover:bg-gray-50 rounded">
                <div className="col-span-2 font-medium text-gray-800">{field.name}</div>
                <div className="col-span-1">
                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{field.type}</span>
                </div>
                <div className="col-span-1">
                  {field.required ? (
                    <span className="text-red-500">Sí</span>
                  ) : (
                    <span className="text-gray-400">No</span>
                  )}
                </div>
                <div className="col-span-4 text-gray-600">{field.description}</div>
                <div className="col-span-4">
                  <code className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{field.example}</code>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nota sobre alertas automáticas */}
      <div className="p-4 bg-amber-50 border-t border-amber-100">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Generación automática de alertas</p>
            <p className="mt-1 text-amber-700">
              Al cargar datos, el sistema generará alertas automáticas para:
              {fileType === 'pacientes' && ' pacientes con más de 90 días de espera y casos de alta prioridad.'}
              {fileType === 'cirugias' && ' cirugías diferidas o canceladas.'}
              {fileType === 'hospitales' && ' hospitales con ocupación superior al 85%.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Upload() {
  // Auth y alertas
  const { user, isAdmin } = useAuthStore()
  const { addUploadAlert, generateAlertsFromData } = useAlertStore()
  const isHospitalUser = user?.rol === ROLES.HOSPITAL_USER
  const isOperador = user?.rol === ROLES.OPERADOR
  const showHospitalContext = (isHospitalUser || isOperador) && user?.hospital_id

  const [file, setFile] = useState(null)
  const [fileType, setFileType] = useState('pacientes')
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [uploadHistory, setUploadHistory] = useState(DEMO_HISTORY)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [generatedAlerts, setGeneratedAlerts] = useState(0)

  // Stats
  const stats = useMemo(() => {
    const total = uploadHistory.length
    const successful = uploadHistory.filter(h => h.status === 'success').length
    const totalRecords = uploadHistory.reduce((sum, h) => sum + (h.records || 0), 0)
    const errors = uploadHistory.filter(h => h.status === 'error').length
    return { total, successful, totalRecords, errors }
  }, [uploadHistory])

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      if (isValidFile(droppedFile)) {
        setFile(droppedFile)
        setResult(null)
      } else {
        toast.error('Tipo de archivo no permitido')
      }
    }
  }, [])

  const isValidFile = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ]
    const validExtensions = ['.xlsx', '.xls', '.csv']
    const extension = '.' + file.name.split('.').pop().toLowerCase()
    return validTypes.includes(file.type) || validExtensions.includes(extension)
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]
      if (isValidFile(selectedFile)) {
        setFile(selectedFile)
        setResult(null)
      } else {
        toast.error('Tipo de archivo no permitido')
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      toast.error('Seleccione un archivo')
      return
    }

    setUploading(true)
    setResult(null)
    setUploadProgress(0)
    setGeneratedAlerts(0)

    // Simular progreso
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) return prev
        return prev + Math.random() * 20
      })
    }, 200)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', fileType)
      // Si es usuario de hospital u operador, agregar el hospital_id
      if (showHospitalContext && user?.hospital_id) {
        formData.append('hospital_id', user.hospital_id)
      }

      // Simular procesamiento (en produccion usar la API real)
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Resultado simulado
      const mockResult = {
        processed: Math.floor(Math.random() * 100) + 50,
        inserted: Math.floor(Math.random() * 50) + 20,
        updated: Math.floor(Math.random() * 30) + 10,
        errors: Math.floor(Math.random() * 5),
      }

      // Simular datos procesados para generar alertas
      const mockProcessedData = generateMockDataForAlerts(fileType, mockResult.processed)

      // Generar alertas automáticas basadas en los datos
      const alertsGenerated = generateAlertsFromData(
        mockProcessedData,
        fileType,
        showHospitalContext ? user?.hospital_id : null
      )
      setGeneratedAlerts(alertsGenerated)

      // Registrar la carga en el sistema de alertas
      const isSuccess = mockResult.errors <= 10
      addUploadAlert(
        isSuccess,
        file.name,
        fileType,
        mockResult.processed,
        isSuccess ? null : 'Errores en procesamiento',
        showHospitalContext ? user?.hospital_id : null
      )

      setUploadProgress(100)
      setResult({ ...mockResult, alertsGenerated })

      // Agregar al historial
      const newEntry = {
        id: Date.now(),
        filename: file.name,
        type: fileType,
        size: `${(file.size / 1024).toFixed(0)} KB`,
        date: new Date().toISOString().split('T')[0],
        records: mockResult.processed,
        status: isSuccess ? 'success' : 'error',
        hospital_id: showHospitalContext ? user?.hospital_id : null,
        hospital_nombre: user?.hospital_nombre || null,
        alertsGenerated,
      }
      setUploadHistory([newEntry, ...uploadHistory])

      toast.success(`Archivo procesado. ${alertsGenerated > 0 ? `Se generaron ${alertsGenerated} alertas.` : ''}`)
      setShowPreview(false)
    } catch (error) {
      // Registrar error en alertas
      addUploadAlert(
        false,
        file.name,
        fileType,
        0,
        error.response?.data?.message || 'Error desconocido',
        showHospitalContext ? user?.hospital_id : null
      )
      toast.error(error.response?.data?.message || 'Error al procesar el archivo')
      setResult({ error: true, message: error.response?.data?.message || 'Error desconocido' })
    } finally {
      clearInterval(progressInterval)
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Generar datos mock para simular alertas (en producción esto vendría del backend)
  const generateMockDataForAlerts = (type, count) => {
    const data = []
    for (let i = 0; i < Math.min(count, 20); i++) {
      if (type === 'pacientes') {
        data.push({
          id: i + 1,
          nombre: `Paciente Demo ${i + 1}`,
          procedimiento: 'Procedimiento de prueba',
          dias_espera: Math.random() > 0.7 ? Math.floor(Math.random() * 60) + 90 : Math.floor(Math.random() * 60) + 30,
          prioridad: Math.random() > 0.8 ? 'Alta' : (Math.random() > 0.5 ? 'Media' : 'Baja'),
          hospital_id: showHospitalContext ? user?.hospital_id : Math.floor(Math.random() * 6) + 1,
        })
      } else if (type === 'cirugias') {
        const estados = ['programada', 'programada', 'programada', 'completada', 'diferida', 'cancelada']
        data.push({
          id: i + 1,
          paciente_nombre: `Paciente ${i + 1}`,
          estado: estados[Math.floor(Math.random() * estados.length)],
          motivo_diferimiento: 'Falta de insumos',
          motivo_cancelacion: 'Condición del paciente',
          hospital_id: showHospitalContext ? user?.hospital_id : Math.floor(Math.random() * 6) + 1,
        })
      } else if (type === 'hospitales') {
        data.push({
          id: i + 1,
          nombre: `Hospital Demo ${i + 1}`,
          ocupacion: Math.random() > 0.6 ? Math.floor(Math.random() * 20) + 80 : Math.floor(Math.random() * 30) + 50,
        })
      }
    }
    return data
  }

  const getFileIcon = (type) => {
    switch (type) {
      case 'pacientes': return Users
      case 'cirugias': return Scissors
      case 'hospitales': return HardDrive
      default: return FileText
    }
  }

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Exitoso</span>
    }
    return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Error</span>
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Carga de Datos</h1>
          <p className="text-gray-500">Importa archivos Excel o CSV al sistema</p>
        </div>

        {/* Info del usuario/hospital */}
        <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${
          showHospitalContext
            ? isOperador ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'
            : 'bg-green-50 border border-green-200'
        }`}>
          {showHospitalContext ? (
            <>
              <Building2 className={`w-5 h-5 ${isOperador ? 'text-amber-600' : 'text-blue-600'}`} />
              <div>
                <p className={`text-xs font-medium ${isOperador ? 'text-amber-600' : 'text-blue-600'}`}>
                  {isOperador ? 'Operador de:' : 'Cargando para:'}
                </p>
                <p className={`text-sm font-semibold ${isOperador ? 'text-amber-800' : 'text-blue-800'}`}>
                  {user?.hospital_nombre || 'Hospital asignado'}
                </p>
              </div>
            </>
          ) : (
            <>
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Modo Administrador</p>
                <p className="text-sm font-semibold text-green-800">Acceso a todos los hospitales</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Cargas Totales"
          value={stats.total}
          subtitle="Archivos procesados"
          icon={UploadIcon}
          color="emerald"
        />
        <KPICard
          title="Exitosas"
          value={stats.successful}
          subtitle="Sin errores"
          icon={CheckCircle}
          color="blue"
        />
        <KPICard
          title="Registros"
          value={stats.totalRecords}
          subtitle="Total importados"
          icon={Database}
          color="purple"
        />
        <KPICard
          title="Con Errores"
          value={stats.errors}
          subtitle="Requieren revision"
          icon={AlertTriangle}
          color={stats.errors > 0 ? 'red' : 'amber'}
          showDashOnZero={true}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tipo de datos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Tipo de datos a cargar</h3>
            <div className="grid grid-cols-3 gap-3">
              {FILE_TYPES.map((type) => {
                const IconComponent = type.icon
                return (
                  <button
                    key={type.id}
                    onClick={() => setFileType(type.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      fileType === type.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <IconComponent className={`w-6 h-6 mb-2 ${
                      fileType === type.id ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <p className={`font-medium ${fileType === type.id ? 'text-emerald-700' : 'text-gray-700'}`}>
                      {type.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Drop Zone */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                dragActive
                  ? 'border-emerald-500 bg-emerald-50'
                  : file
                  ? 'border-emerald-300 bg-emerald-50/50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />

              {file ? (
                <div className="space-y-4">
                  <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-2xl flex items-center justify-center">
                    <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-lg">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => { setFile(null); setResult(null); }}
                      className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </button>
                    <button
                      onClick={() => setShowPreview(true)}
                      className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <UploadIcon className="w-4 h-4" />
                      Cargar archivo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-20 h-20 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center">
                    <UploadIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <div>
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer text-emerald-600 hover:text-emerald-700 font-semibold text-lg"
                    >
                      Selecciona un archivo
                    </label>
                    <p className="text-gray-500 mt-1">o arrastra y suelta aqui</p>
                  </div>
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <FileSpreadsheet className="w-4 h-4" />
                      .xlsx
                    </span>
                    <span className="flex items-center gap-1">
                      <Table className="w-4 h-4" />
                      .xls
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      .csv
                    </span>
                  </div>
                </div>
              )}

              {/* Progress bar */}
              {uploading && (
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Procesando... {Math.round(uploadProgress)}%</p>
                </div>
              )}
            </div>
          </div>

          {/* Result */}
          {result && !result.error && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 border-l-4 border-l-emerald-500">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-4">Procesamiento completado</h3>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-gray-800">{result.processed}</p>
                      <p className="text-xs text-gray-500">Procesados</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{result.inserted}</p>
                      <p className="text-xs text-gray-500">Insertados</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{result.updated}</p>
                      <p className="text-xs text-gray-500">Actualizados</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                      <p className="text-xs text-gray-500">Errores</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-amber-600">{result.alertsGenerated || 0}</p>
                      <p className="text-xs text-gray-500">Alertas</p>
                    </div>
                  </div>
                  {result.alertsGenerated > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-800">
                        Se generaron <strong>{result.alertsGenerated} alertas</strong> automaticas.
                        <a href="/alertas" className="ml-1 text-amber-700 underline hover:text-amber-900">Ver alertas</a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {result && result.error && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 border-l-4 border-l-red-500">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Error en el procesamiento</h3>
                  <p className="text-red-600">{result.message}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Historial de cargas</h3>
            <p className="text-sm text-gray-500">{uploadHistory.length} archivos</p>
          </div>

          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {uploadHistory.length === 0 ? (
              <div className="p-8 text-center">
                <UploadIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay cargas recientes</p>
              </div>
            ) : (
              uploadHistory.map((item) => {
                const IconComponent = getFileIcon(item.type)
                return (
                  <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        item.status === 'success' ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        <IconComponent className={`w-4 h-4 ${
                          item.status === 'success' ? 'text-emerald-600' : 'text-red-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">{item.filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(item.status)}
                          <span className="text-xs text-gray-500">{item.records} registros</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.date}
                          </span>
                          <span>{item.size}</span>
                        </div>
                        {item.error && (
                          <p className="text-xs text-red-500 mt-1">{item.error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <button className="w-full flex items-center justify-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
              Ver historial completo
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Instrucciones y Plantillas */}
      <FormatInstructions fileType={fileType} />

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          file={file}
          fileType={fileType}
          onClose={() => setShowPreview(false)}
          onConfirm={handleUpload}
          uploading={uploading}
        />
      )}
    </div>
  )
}
